import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { Alert, Button, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  fetchBillingInformation,
  fetchCardSummary,
} from '@/features/billing/payment-method/paymentMethodActions'
import {
  cardExpiryLabel,
  cardNameLabel,
  savedCopy,
} from '@/features/billing/payment-method/paymentMethodCopy'
import type {
  BillingInformation,
  CardSummary,
} from '@/features/billing/payment-method/payment-method.types'
import { BILLING_INFORMATION_PATH, PAYMENT_METHOD_PATH } from './paths'

const isCardSummary = (value: unknown): value is CardSummary =>
  typeof value === 'object' &&
  value !== null &&
  'brand' in value &&
  'last4' in value

interface RouterState {
  savedCard?: CardSummary
  billingSaved?: boolean
}

const readRouterState = (state: unknown): RouterState => {
  if (typeof state !== 'object' || state === null) return {}
  const savedCard =
    'savedCard' in state && isCardSummary(state.savedCard)
      ? state.savedCard
      : undefined
  const billingSaved = 'billingSaved' in state && state.billingSaved === true
  return { savedCard, billingSaved }
}

type FetchStatus = 'loading' | 'loaded' | 'no_customer' | 'failed'

// The card summary answers null with no Stripe customer, so only the billing information 400 means nothing to update.
const failureStatus = (error: unknown): FetchStatus =>
  isAxiosError(error) && error.response?.status === 400
    ? 'no_customer'
    : 'failed'

const formattedAddress = (
  address: BillingInformation['address']
): string | null => {
  if (!address) return null
  const statePostal = [address.state, address.postalCode]
    .filter(Boolean)
    .join(' ')
  return [
    address.line1,
    address.line2,
    address.city,
    statePostal,
    address.country,
  ]
    .filter(Boolean)
    .join(', ')
}

export const PaymentMethodBlock = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [{ savedCard, billingSaved }] = useState(() =>
    readRouterState(location.state)
  )
  const [card, setCard] = useState<CardSummary | null>(savedCard ?? null)
  const [cardStatus, setCardStatus] = useState<FetchStatus>(
    savedCard ? 'loaded' : 'loading'
  )
  const [billingInformation, setBillingInformation] =
    useState<BillingInformation | null>(null)
  const [billingStatus, setBillingStatus] = useState<FetchStatus>('loading')

  useEffect(() => {
    // Router state survives reload and back, which would replay the success notice.
    const carried = readRouterState(location.state)
    if (!carried.savedCard && !carried.billingSaved) return
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    })
  }, [location.state, location.pathname, location.search, navigate])

  useEffect(() => {
    fetchCardSummary()(dispatch)
      .then((summary) => {
        setCard(summary)
        setCardStatus('loaded')
      })
      .catch((error: unknown) => {
        setCardStatus(failureStatus(error))
      })
  }, [dispatch])

  useEffect(() => {
    fetchBillingInformation()(dispatch)
      .then((information) => {
        setBillingInformation(information)
        setBillingStatus('loaded')
      })
      .catch((error: unknown) => {
        setBillingInformation(null)
        setBillingStatus(failureStatus(error))
      })
  }, [dispatch])

  const address =
    billingInformation && formattedAddress(billingInformation.address)
  const showNoCard =
    !card && (cardStatus === 'loaded' || cardStatus === 'no_customer')
  const noCustomer =
    cardStatus === 'no_customer' || billingStatus === 'no_customer'
  // A failed subscription default update leaves Stripe on the old card, which the refetch reveals.
  const savedCardOnFile =
    savedCard &&
    card?.brand === savedCard.brand &&
    card.last4 === savedCard.last4

  return (
    <div className="flex flex-col gap-3">
      <Text variant={TypographyStyles.EYEBROW}>Payment method</Text>
      {savedCardOnFile && (
        <Alert variant="success">{savedCopy(savedCard)}</Alert>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {card && (
          <div className="flex flex-col gap-0.5">
            <Text variant={TypographyStyles.LABEL_SM}>
              {cardNameLabel(card)}
            </Text>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {cardExpiryLabel(card)}
            </Text>
          </div>
        )}
        {showNoCard && (
          <Text variant={TypographyStyles.LABEL_SM}>No card on file</Text>
        )}
        {!card && cardStatus === 'failed' && (
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            We couldn&apos;t load your card. Refresh to try again.
          </Text>
        )}
        {!noCustomer && (
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            aria-label="Update default payment method"
            onClick={() => navigate(PAYMENT_METHOD_PATH)}
          >
            Update
          </Button>
        )}
      </div>
      {(billingStatus === 'loaded' || billingStatus === 'failed') && (
        <div className="flex flex-col gap-3">
          <Text variant={TypographyStyles.EYEBROW}>Billing information</Text>
          {billingSaved && (
            <Alert variant="success">Billing information saved.</Alert>
          )}
          <div className="flex flex-wrap items-start justify-between gap-4">
            {billingInformation && (
              <div className="flex flex-col gap-0.5">
                {billingInformation.email && (
                  <Text variant={TypographyStyles.LABEL_SM}>
                    {billingInformation.email}
                  </Text>
                )}
                {address && (
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                  >
                    {address}
                  </Text>
                )}
              </div>
            )}
            {billingStatus === 'failed' && (
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                We couldn&apos;t load your billing information. Refresh to try
                again.
              </Text>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto"
              aria-label="Update billing information"
              onClick={() => navigate(BILLING_INFORMATION_PATH)}
            >
              Update
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
